////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// 🟢 [Subsection 20: NEON GREEN] III. IMPORTS - Rob, importing React hooks!
import { useState, useCallback, useEffect } from 'react';
// 🟢 [Subsection 21: NEON GREEN] Rob, importing Pea_Security for presentation layer!
import Pea_Security from '../pea/Bitsbound_Kings_Home_Primary_SRC_Security_Pea.js';

// ⚪ [Subsection 1: PURE WHITE] I. MANDATORY FILE HEADER
/**
 * ⚪ [Subsection 2: PEARL] FILE: Pod_Security_SRC_Primary_Home_Kings.tsx
 * ⚪ [Subsection 3: SNOW] DESCRIPTION: Security statement logic component (Pod - logic only)
 * ⚪ [Subsection 4: IVORY] AUTHOR: BitsBound
 * ⚪ [Subsection 5: CREAM] CREATED: 2025
 * ⚪ [Subsection 10: GHOST] Copyright © 2025 BitsBound. All rights reserved.
 */

// 🔵 [Subsection 31: BRIGHT BLUE] IV. INDEPENDENT CONSTANTS - Rob, SECURITY_CONTENT holds all security text!
const SECURITY_CONTENT = {
  overview: {
    title: "Security at BitsBound",
    content: `BitsBound is built with security at its core. As a contract analysis platform handling sensitive legal documents, we implement enterprise-grade security measures to protect your data at every layer.

Our Commitment:
• Two-factor authentication (2FA) for account security
• HTTPS/TLS encryption for all data in transit
• AES-256 encryption for data at rest
• Zero credit card data on our servers (Stripe handles all payment processing)
• Customer data isolation architecture
• Fail-fast security architecture (services refuse to start without proper credentials)
• Continuous security monitoring`,
  },
  compliance: {
    title: "Compliance & Standards",
    content: `Regulatory Compliance:
• GDPR compliant data handling for EU customers
• CCPA compliant for California residents
• Privacy-by-design architecture

Payment Security:
• All payments processed through Stripe (PCI DSS Level 1 certified)
• No credit card numbers stored on BitsBound infrastructure
• Webhook signature verification for all payment events

Data Handling:
• Data Processing Agreements available upon request
• Clear data retention and deletion policies
• Right to data portability and erasure honored`,
  },
  infrastructure: {
    title: "Infrastructure Security",
    content: `Cloud Architecture:
• Hosted on Vercel (frontend) with automatic SSL/TLS
• Backend services on Render with managed infrastructure (no request timeouts for AI analysis)
• MongoDB Atlas with encryption at rest and in transit
• Cloudflare R2 for secure document storage (zero egress fees)

Credential Management:
• All secrets stored in environment variables only
• No hardcoded credentials in source code
• Fail-fast startup validation (missing credentials = immediate failure)
• Git-ignored environment files (.env never committed)

Network Security:
• HTTPS enforced on all endpoints
• TLS 1.2+ required for all connections
• Production domain whitelist for CORS (bitsbound.com, www.bitsbound.com only)
• Development CORS requires explicit opt-in environment variable
• DDoS protection via Cloudflare`,
  },
  "data-protection": {
    title: "Data Protection",
    content: `Encryption:
• TLS 1.2+ encryption for all data in transit
• AES-256 encryption for stored documents
• SHA-256 hashing for data integrity verification
• Cryptographically secure token generation

Input Security:
• DOMPurify sanitization for all user inputs
• Protection against XSS, SQL injection, and XXE attacks
• Strict input validation on all form submissions
• Parameterized database queries (MongoDB)

Document Security:
• Contracts stored in Cloudflare R2 with customer-isolated keys
• Documents processed in-memory, not persisted beyond analysis
• AI processing isolated per-request (no cross-customer data leakage)`,
  },
  "access-control": {
    title: "Access Control",
    content: `Authentication:
• Two-factor authentication (2FA) support
• Secure token-based authentication
• API key authentication for administrative endpoints
• Stripe webhook signature verification
• Password hashing (credentials never stored in plaintext)

Authorization:
• Customer data isolation at database level (MongoDB customer partitioning)
• Role-based access controls
• Audit logging of administrative actions
• Session management with secure token rotation

CORS Policy:
• Strict origin whitelist (production domains only)
• No wildcard CORS in production
• Requests from unauthorized origins are blocked and logged`,
  },
  privacy: {
    title: "Privacy",
    content: `Your Data, Your Control:
• We never sell your data to third parties
• Contract documents processed only for your analysis
• AI processing does not train on your documents
• Clear data retention policies with deletion on request

AI & Data Processing:
• Claude AI analysis is stateless (your contracts are not used for model training)
• Analysis results stored only in your account
• Full document deletion available on request

Transparency:
• Privacy Policy details all data practices
• Cookie usage limited to essential functionality
• No third-party tracking or advertising
• Data Processing Agreements available for enterprise`,
  },
  contact: {
    title: "Security Contact",
    content: `Report Security Issues:
We take security vulnerabilities seriously. If you discover a potential security issue, please report it to our team immediately.

Contact:
Email: admin@bitsbound.com

Responsible Disclosure:
We appreciate security researchers who report vulnerabilities responsibly. Please allow us reasonable time to investigate and address any issues before public disclosure. We're committed to acknowledging reports promptly and keeping you informed of our progress.`,
  },
};

// 🔵 [Subsection 45: INDIGO] V. FUNCTION/CLASS NAME - Rob, main Pod function!
function Pod_Security() {
  // 🟣 [Subsection 51: BRIGHT PURPLE] VI. DECLARATIONS - Rob, state management section!
  const [activeSection, setActiveSection] = useState("overview");

  // 🟠 [Subsection 71: BRIGHT ORANGE] VIII. OPERATIONS - Rob, handler functions!
  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(section);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownload = useCallback(() => {
    const securityText = Object.entries(SECURITY_CONTENT)
      .map(([key, section]) => `${section.title}\n${'='.repeat(section.title.length)}\n${section.content}`)
      .join("\n\n");
    const blob = new Blob([securityText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "BitsBound_Security_Statement.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // 🔴 [Subsection 81: FIRE RED] IX. PRE-RENDER LOGIC - Rob, log page view!
  useEffect(() => {
    console.info("[Security] Page viewed");
  }, []);

  // ⚫ [Subsection 91: OBSIDIAN] X. RENDER/RETURN - Rob, returning Pea with all props!
  return (
    <Pea_Security
      activeSection={activeSection}
      securityContent={SECURITY_CONTENT}
      onSectionChange={handleSectionChange}
      onPrint={handlePrint}
      onDownload={handleDownload}
    />
  );
}

// ⚫ [Subsection 95: ONYX] XI. EXPORTS
export default Pod_Security;